import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';

import api from '../../../utils/axios';
import LetterSectionsPanel from '../../../components/letterSections/LetterSectionsPanel';

const POLL_MS = 5000;
const MAX_POLL_ATTEMPTS = 60; // ~5 minutes

/**
 * Step 4 of the MinimalAppeal flow (LTR-08). The legacy Edit/Preview tabs,
 * markdown textarea, raw-HTML `<pre>` preview, template-paste toolbar and
 * free-text "revise" box are gone — editing, autosave, approval, PDF preview
 * and download now live inside the shared LetterSectionsPanel, and
 * regeneration targets a single section via section_keys.
 *
 * The panel is mounted with letterType="minimal-appeal" and the CURRENT
 * MinimalAppealLetter id (appeal.letter.id) — NOT the MinimalAppeal id. Per
 * CLAUDE.md effect safety the section fetch is keyed by that letter id plus an
 * explicit refresh key that only bumps once when a regenerate completes.
 */
const ReviewStep = ({ appeal, onNext, onBack, onUpdate, onError, onSuccess }) => {
  const [letterId, setLetterId] = useState(appeal?.letter?.id || null);
  const [letterVersion, setLetterVersion] = useState(appeal?.letter?.version || 1);
  const [generationStatus, setGenerationStatus] = useState('idle');
  const [sectionsRefreshKey, setSectionsRefreshKey] = useState(0);
  const pollingRef = useRef(null);

  // Keep the letter id/version in sync with the current letter as the parent
  // hands down fresh appeal data (e.g. after navigation back into this step).
  useEffect(() => {
    if (appeal?.letter?.id) setLetterId(appeal.letter.id);
    if (appeal?.letter?.version) setLetterVersion(appeal.letter.version);
  }, [appeal?.letter?.id, appeal?.letter?.version]);

  // Clean up the regenerate poll on unmount.
  useEffect(
    () => () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    },
    []
  );

  // Poll the appeal detail after a section regenerate until a newer letter
  // version arrives, then bump the panel refresh key exactly once.
  const pollForRegenerated = useCallback(
    (fromVersion) => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      let attempts = 0;
      pollingRef.current = setInterval(async () => {
        attempts += 1;
        try {
          const { data: updatedAppeal } = await api.get(`/documents/minimal-appeals/${appeal.id}/`);
          if (updatedAppeal?.letter && updatedAppeal.letter.version > fromVersion) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setLetterId(updatedAppeal.letter.id);
            setLetterVersion(updatedAppeal.letter.version);
            setGenerationStatus('generated');
            setSectionsRefreshKey((k) => k + 1);
            onUpdate?.(updatedAppeal);
            onSuccess?.('Section regenerated.');
          } else if (attempts >= MAX_POLL_ATTEMPTS) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setGenerationStatus('idle');
            onError?.('Regeneration timed out. Please check back later or try again.');
          }
        } catch (_e) {
          // transient — keep polling until MAX_POLL_ATTEMPTS
        }
      }, POLL_MS);
    },
    [appeal, onUpdate, onSuccess, onError]
  );

  const handleRegenerateSection = useCallback(
    async (sectionKey) => {
      if (!appeal?.id) return;
      onError?.(null);
      try {
        await api.post(`/documents/minimal-appeals/${appeal.id}/regenerate/`, {
          section_keys: [sectionKey],
          model: 'gemini-2.5-flash',
        });
        setGenerationStatus('generating');
        pollForRegenerated(letterVersion);
      } catch (err) {
        onError?.(err.response?.data?.error || 'Failed to queue regeneration');
      }
    },
    [appeal, letterVersion, pollForRegenerated, onError]
  );

  if (!appeal || !appeal.letter) {
    return (
      <Box>
        <Alert severity="warning">
          No letter content found. Please go back and generate an appeal letter first.
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button variant="outlined" onClick={onBack} size="large">
            Back: Generate Letter
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Step 4: Review & Edit Letter
      </Typography>

      <Typography variant="body1" color="textSecondary" paragraph>
        Review and edit each section of your appeal letter. Changes save automatically;
        use Regenerate section to redraft a single section without affecting the others.
      </Typography>

      <LetterSectionsPanel
        letterType="minimal-appeal"
        letterId={letterId}
        sectionsRefreshKey={sectionsRefreshKey}
        generationStatus={generationStatus}
        onRegenerateSection={handleRegenerateSection}
        onAlert={(msg) => onError?.(msg)}
      />

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" onClick={onBack} size="large">
          Back: Generate Letter
        </Button>

        <Button variant="contained" onClick={onNext} size="large">
          Next: Export & Send
        </Button>
      </Box>
    </Box>
  );
};

export default ReviewStep;
